import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container, Box, Typography, Button, CircularProgress, Alert, Card, CardContent, Chip, Divider,
} from '@mui/material';
import { ArrowBack, Share } from '@mui/icons-material';
import ScoutThreadView from '../../components/scout/ScoutThreadView';
import ScoutActionButtons from '../../components/scout/ScoutActionButtons';
import { scoutService } from '../../services/scoutService';

const ScoutThreadPage: React.FC = () => {
  const { threadId } = useParams<{ threadId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [thread, setThread] = useState<any>(null);
  const [messages, setMessages] = useState<any[]>([]);
  const [sharingContact, setSharingContact] = useState(false);

  const loadThread = useCallback(async () => {
    if (!threadId) return;
    try {
      const data = await scoutService.getThread(threadId);
      setThread(data.thread);
      setMessages(data.messages);
    } catch (err: any) {
      setError(err.response?.data?.message || 'スレッドの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [threadId]);

  useEffect(() => {
    loadThread();
  }, [loadThread]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button variant="outlined" onClick={() => navigate('/athlete/scouts')}>受信箱に戻る</Button>
      </Container>
    );
  }

  const isAccepted = thread?.status === 'accepted';
  const isDisabled = !isAccepted;

  const handleShareContact = async () => {
    if (!threadId) return;
    setSharingContact(true);
    try {
      await scoutService.shareContact(threadId);
      loadThread();
    } catch (err) {
      console.error('Contact share failed:', err);
    } finally {
      setSharingContact(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      <Box sx={{ bgcolor: '#0066cc', color: 'white', py: 3 }}>
        <Container maxWidth="md">
          <Button startIcon={<ArrowBack />} onClick={() => navigate('/athlete/scouts')}
            sx={{ color: 'white', mb: 1 }}>
            受信箱
          </Button>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {thread?.subject}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
            <Typography variant="body2">{thread?.companyName}</Typography>
            <Chip label={thread?.status} size="small" sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)' }} variant="outlined" />
          </Box>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: 4 }}>
        {/* アクションボタン（承諾/辞退/ブロック） */}
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
            {!isAccepted && (
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>企業とのやり取りを開始する</Typography>
            )}
            <ScoutActionButtons
              threadId={threadId!}
              status={thread?.status}
              onActionDone={loadThread}
            />
            {isAccepted && (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>企業と直接やりとりをする</Typography>
                {thread?.contactShared ? (
                  <Button
                    variant="outlined"
                    startIcon={<Share />}
                    disabled
                    sx={{ color: '#999', borderColor: '#ccc' }}
                  >
                    連絡先を共有済み
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    startIcon={<Share />}
                    onClick={handleShareContact}
                    disabled={sharingContact}
                    sx={{ bgcolor: '#ff9800', '&:hover': { bgcolor: '#f57c00' } }}
                  >
                    {sharingContact ? '共有中...' : '連絡先を共有する'}
                  </Button>
                )}
              </Box>
            )}
          </CardContent>
        </Card>

        <Divider sx={{ mb: 3 }} />

        {/* メッセージスレッド */}
        {!isAccepted && !['blocked', 'declined'].includes(thread?.status) && (
          <Alert severity="info" sx={{ mb: 2 }}>
            メッセージを送信するにはスカウトを承諾してください。
          </Alert>
        )}
        <ScoutThreadView
          threadId={threadId!}
          messages={messages}
          role="athlete"
          disabled={isDisabled}
          onMessageSent={loadThread}
        />
      </Container>
    </Box>
  );
};

export default ScoutThreadPage;
